import { useMemo } from "react";
import { Calendar, MessageCircle, ArrowDownToLine, ArrowUpFromLine, Activity } from "lucide-react";

/**
 * DisputeHealthCard — mini-graphique de santé d'un litige.
 * Affiche en 4 tuiles compactes :
 *  - Âge du dossier (jours depuis création)
 *  - Nombre total d'échanges
 *  - Date du dernier échange Nous (ce qu'on a fait)
 *  - Date du dernier échange Le tiers (ce qu'ils ont répondu)
 *
 * Affiche aussi un indicateur visuel "santé" :
 *  - ✅ healthy : échange du tiers récent (< 14j)
 *  - ⚠️ stalling : pas de réponse du tiers depuis 14-30j alors qu'on a relancé
 *  - 🔴 frozen : pas d'activité depuis > 30j
 */
const daysBetween = (isoA, isoB) => {
  if (!isoA || !isoB) return null;
  const a = new Date(isoA);
  const b = new Date(isoB);
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

const relativeShort = (iso) => {
  if (!iso) return "—";
  const days = daysBetween(iso, new Date().toISOString());
  if (days === null) return "—";
  if (days === 0) return "Aujourd'hui";
  if (days === 1) return "Hier";
  if (days < 30) return `il y a ${days} j`;
  if (days < 365) return `il y a ${Math.round(days / 30)} mois`;
  return `il y a ${Math.round(days / 365)} an(s)`;
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
};

export const DisputeHealthCard = ({ dispute, history = [] }) => {
  const stats = useMemo(() => {
    const createdAt = dispute?.created_at || dispute?.opened_at || null;
    const ageDays = createdAt ? daysBetween(createdAt, new Date().toISOString()) : null;
    const sorted = [...history].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const lastUs = [...sorted].reverse().find((e) => e.origin === "us") || null;
    const lastThird = [...sorted].reverse().find((e) => e.origin === "third_party") || null;
    const lastActivity = sorted.length > 0 ? sorted[sorted.length - 1] : null;
    const daysSinceLastActivity = lastActivity?.date ? daysBetween(lastActivity.date, new Date().toISOString()) : null;
    const daysSinceLastThird = lastThird?.date ? daysBetween(lastThird.date, new Date().toISOString()) : null;

    let health = "neutral";
    let healthLabel = "Démarre";
    if (sorted.length === 0) {
      health = "neutral"; healthLabel = "Aucun échange";
    } else if (daysSinceLastActivity !== null && daysSinceLastActivity > 30) {
      health = "frozen"; healthLabel = "Dossier figé";
    } else if (daysSinceLastThird !== null && daysSinceLastThird > 14 && lastActivity?.origin === "us") {
      health = "stalling"; healthLabel = "Sans réponse";
    } else if (sorted.length >= 1) {
      health = "healthy"; healthLabel = "Actif";
    }

    return {
      ageDays, exchangesCount: sorted.length, lastUs, lastThird, lastActivity,
      daysSinceLastActivity, daysSinceLastThird, health, healthLabel,
    };
  }, [dispute, history]);

  const healthClass = {
    healthy: "bg-emerald-50 border-emerald-200 text-emerald-700",
    stalling: "bg-amber-50 border-amber-200 text-amber-800",
    frozen: "bg-red-50 border-red-200 text-red-700",
    neutral: "bg-zinc-50 border-zinc-200 text-zinc-600",
  }[stats.health];

  const healthDot = {
    healthy: "bg-emerald-500",
    stalling: "bg-amber-500",
    frozen: "bg-red-500",
    neutral: "bg-zinc-400",
  }[stats.health];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="dispute-health">
      <Tile
        icon={<Calendar className="h-3.5 w-3.5" />}
        label="Âge dossier"
        primary={stats.ageDays !== null ? `${stats.ageDays} j` : "—"}
        secondary={dispute?.created_at ? fmtDate(dispute.created_at) : null}
        testId="health-age"
      />
      <Tile
        icon={<MessageCircle className="h-3.5 w-3.5" />}
        label="Échanges"
        primary={stats.exchangesCount}
        secondary={stats.lastActivity ? `dernier · ${relativeShort(stats.lastActivity.date)}` : "aucun"}
        testId="health-exchanges"
      />
      <Tile
        icon={<ArrowUpFromLine className="h-3.5 w-3.5 text-blue-600" />}
        label="Dernier · Nous"
        primary={stats.lastUs ? relativeShort(stats.lastUs.date) : "—"}
        secondary={stats.lastUs?.date ? fmtDate(stats.lastUs.date) : "jamais"}
        testId="health-last-us"
      />
      <Tile
        icon={<ArrowDownToLine className="h-3.5 w-3.5 text-purple-600" />}
        label="Dernier · Tiers"
        primary={stats.lastThird ? relativeShort(stats.lastThird.date) : "—"}
        secondary={stats.lastThird?.date ? fmtDate(stats.lastThird.date) : "jamais"}
        testId="health-last-third"
      />
      <div
        className={`rounded-xl border p-2.5 flex flex-col justify-center ${healthClass}`}
        data-testid="health-status"
      >
        <div className="text-[10px] uppercase tracking-wide font-semibold opacity-80 flex items-center gap-1">
          <Activity className="h-3 w-3" /> Santé
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`h-2 w-2 rounded-full ${healthDot} animate-pulse`} />
          <span className="text-sm font-semibold">{stats.healthLabel}</span>
        </div>
      </div>
    </div>
  );
};

const Tile = ({ icon, label, primary, secondary, testId }) => (
  <div className="rounded-xl border border-zinc-200 bg-white p-2.5" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500 flex items-center gap-1">
      {icon} {label}
    </div>
    <div className="text-sm font-bold text-zinc-900 tabular-nums mt-0.5">{primary}</div>
    {secondary && <div className="text-[10px] text-zinc-400 truncate">{secondary}</div>}
  </div>
);

export default DisputeHealthCard;
